import React, {useCallback, useEffect, useState} from 'react';
import {Box, Checkbox, Stack, TextField, Tooltip, Typography} from "@mui/material";
import FormControlLabel from "@mui/material/FormControlLabel";
import CuttingImageGallery from "../../../pages/organization/cutting/CuttingImageGallery";
import ModalImageGallery from "../../../pages/organization/cutting/ModalImageGallery";
import CuttingTextField from "../textfield/CuttingTextField";
import {DETAIL_OPACITY, HATCH_OPACITY} from "../../../utils/cutting/drawCuttingMap";

const formatRulerValue = (value) => {
    if (value == null || Number.isNaN(value)) {
        return '';
    }
    return Number(value).toFixed(1);
};

const clampRulerCoord = (value, max) => {
    if (!Number.isFinite(value)) {
        return null;
    }
    const upper = Number.isFinite(max) && max > 0 ? max : value;
    return Math.min(upper, Math.max(0, value));
};

const ActiveDetailPreview = ({detail}) => {
    const ratio = detail ? detail.width / detail.height : 1;
    const isCutOff = detail?.previewType === 'cutOff';
    const previewStyle = ratio >= 3
        ? {width: '78%', height: 'auto'}
        : {width: 'auto', height: '78%'};

    return (
        <Box
            sx={{
                height: 300,
                border: '2px solid #3B8BEC',
                boxSizing: 'border-box',
                display: 'flex',
                flexDirection: 'column',
                p: 2,
                mb: 2,
            }}
        >
            <Box sx={{position: 'relative', display: 'flex', alignItems: 'center', mb: 1, minHeight: 24}}>
                <Typography color="primary" variant="subtitle1">
                    Активная деталь
                </Typography>
                {detail && (
                    <Typography
                        variant="body1"
                        sx={{
                            position: 'absolute',
                            left: '50%',
                            transform: 'translateX(-50%)',
                            wordBreak: 'break-word',
                            lineHeight: 1.2,
                        }}
                    >
                        {detail.label?.text}
                    </Typography>
                )}
            </Box>
            {detail ? (
                <Box sx={{flex: 1, minHeight: 0, display: 'flex', alignItems: 'center', justifyContent: 'center'}}>
                    <Box
                        sx={{
                            ...previewStyle,
                            minWidth: 24,
                            minHeight: 24,
                            maxWidth: '78%',
                            maxHeight: '78%',
                            aspectRatio: `${detail.width} / ${detail.height}`,
                            border: `1px solid ${isCutOff ? '#B8B8B8' : '#1A1A1A'}`,
                            position: 'relative',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                            p: 1,
                            boxSizing: 'border-box',
                            overflow: 'hidden',
                        }}
                    >
                        <Box
                            sx={{
                                position: 'absolute',
                                inset: 0,
                                bgcolor: isCutOff ? '#fff' : detail.color,
                                opacity: isCutOff ? 1 : DETAIL_OPACITY,
                            }}
                        />
                        {isCutOff && (
                            <Box
                                sx={{
                                    position: 'absolute',
                                    inset: 0,
                                    backgroundImage: `repeating-linear-gradient(-45deg, transparent 0, transparent 8px, ${detail.hatchColor} 8px, ${detail.hatchColor} 10px)`,
                                    opacity: HATCH_OPACITY,
                                }}
                            />
                        )}
                    </Box>
                </Box>
            ) : (
                <Typography variant="body2" color="text.secondary">
                    Наведите курсор на деталь на карте
                </Typography>
            )}
        </Box>
    );
};

const CuttingModule = ({
                           images,
                           currentPage,
                           setCurrentPage,
                           isLoading,
                           fitToStripFirst,
                           setFitToStripFirst,
                           fitToStripSecond,
                           setFitToStripSecond,
                           dimensions,
                           setDimensions,
                           combiningDrops,
                           setCombiningDrops,
                           cutDropDetail,
                           setCutDropDetail,
                           cutFitToStripFirstDropDetail,
                           setCutFitToStripFirstDropDetail,
                           cutFitToStripSecondDropDetail,
                           setCutFitToStripSecondDropDetail,
                           cutCombiningDrops,
                           setCutCombiningDrops,
                           takeAccountDimensions,
                           setTakeAccountDimensions,
                           cutWastes,
                           setCutWastes,
                           cutCutOffs,
                           setCutCutOffs,
                           cutOnlyCutOffs,
                           setCutOnlyCutOffs,
                           cutOnlyWarehouse,
                           setCutOnlyWarehouse,
                           vectorMode = false,
                           vectorCuttingMap = null,
                           vectorPageCount = 0,
                           drawEdge = true,
                       }) => {

    const [openModalImageGallery, setOpenModalImageGallery] = useState(false);
    const [activeDetail, setActiveDetail] = useState(null);
    const [showRuler, setShowRuler] = useState(false);
    const [rulerCoords, setRulerCoords] = useState(null);
    const [rulerInputX, setRulerInputX] = useState('');
    const [rulerInputY, setRulerInputY] = useState('');
    const [rulerEditing, setRulerEditing] = useState(false);
    const [rulerPinned, setRulerPinned] = useState(false);

    useEffect(() => {
        setActiveDetail(null);
        setRulerCoords(null);
        setRulerInputX('');
        setRulerInputY('');
        setRulerEditing(false);
        setRulerPinned(false);
    }, [currentPage, vectorCuttingMap]);

    useEffect(() => {
        if (!showRuler) {
            setRulerPinned(false);
            setRulerEditing(false);
        }
    }, [showRuler]);

    useEffect(() => {
        if (!rulerEditing && rulerCoords) {
            setRulerInputX(formatRulerValue(rulerCoords.x));
            setRulerInputY(formatRulerValue(rulerCoords.y));
        }
    }, [rulerCoords, rulerEditing]);

    const rulerMaxX = Number(vectorCuttingMap?.length) || 0;
    const rulerMaxY = Number(vectorCuttingMap?.width) || 0;

    const applyRulerInputs = (nextX = rulerInputX, nextY = rulerInputY) => {
        const x = clampRulerCoord(Number(String(nextX).replace(',', '.')), rulerMaxX);
        const y = clampRulerCoord(Number(String(nextY).replace(',', '.')), rulerMaxY);
        if (x != null && y != null) {
            setRulerCoords({x, y});
            setRulerInputX(formatRulerValue(x));
            setRulerInputY(formatRulerValue(y));
            setRulerPinned(true);
        }
        setRulerEditing(false);
    };

    const handleRulerInputChange = (axis, rawValue) => {
        setRulerEditing(true);
        const nextX = axis === 'x' ? rawValue : rulerInputX;
        const nextY = axis === 'y' ? rawValue : rulerInputY;
        if (axis === 'x') {
            setRulerInputX(rawValue);
        } else {
            setRulerInputY(rawValue);
        }
        const x = clampRulerCoord(Number(String(nextX).replace(',', '.')), rulerMaxX);
        const y = clampRulerCoord(Number(String(nextY).replace(',', '.')), rulerMaxY);
        if (x != null && y != null && rawValue !== '' && rawValue !== '-') {
            setRulerCoords({x, y});
            setRulerPinned(true);
        }
    };

    const rulerEditingRef = React.useRef(false);
    const rulerPinnedRef = React.useRef(false);
    useEffect(() => {
        rulerEditingRef.current = rulerEditing;
    }, [rulerEditing]);
    useEffect(() => {
        rulerPinnedRef.current = rulerPinned;
    }, [rulerPinned]);

    const onRulerCoordsChange = useCallback((coords) => {
        if (rulerEditingRef.current || rulerPinnedRef.current) {
            return;
        }
        const x = clampRulerCoord(coords?.x, rulerMaxX);
        const y = clampRulerCoord(coords?.y, rulerMaxY);
        if (x != null && y != null) {
            setRulerCoords({x, y});
        }
    }, [rulerMaxX, rulerMaxY]);

    const onRulerPinToggle = useCallback((coords) => {
        setRulerEditing(false);
        if (rulerPinnedRef.current) {
            setRulerPinned(false);
            return;
        }
        const x = clampRulerCoord(coords?.x, rulerMaxX);
        const y = clampRulerCoord(coords?.y, rulerMaxY);
        if (x != null && y != null) {
            setRulerCoords({x, y});
        }
        setRulerPinned(true);
    }, [rulerMaxX, rulerMaxY]);

    const handleCheckbox = (checkbox) => {
        switch (checkbox) {
            case 'fitToStripFirst':
                setFitToStripFirst(!fitToStripFirst);
                break;
            case 'fitToStripSecond':
                setFitToStripSecond(!fitToStripSecond);
                break;
            case 'combiningDrops':
                setCombiningDrops(!combiningDrops);
                break;
            case 'cutDropDetail':
                setCutDropDetail(!cutDropDetail);
                break;
            case 'cutFitToStripFirstDropDetail':
                setCutFitToStripFirstDropDetail(!cutFitToStripFirstDropDetail);
                break;
            case 'cutFitToStripSecondDropDetail':
                setCutFitToStripSecondDropDetail(!cutFitToStripSecondDropDetail);
                break;
            case 'cutCombiningDrops':
                setCutCombiningDrops(!cutCombiningDrops);
                break;
            case 'takeAccountDimensions':
                setTakeAccountDimensions(!takeAccountDimensions);
                break;
            case 'cutWastes':
                setCutWastes(!cutWastes);
                break;
            case 'cutCutOffs':
                setCutCutOffs(!cutCutOffs);
                break;
            case 'сutOnlyCutOffs':
                setCutOnlyCutOffs(!cutOnlyCutOffs);
                break;
            case 'cutOnlyWarehouse':
                setCutOnlyWarehouse(!cutOnlyWarehouse);
                break;
            default:
                break;
        }
    }

    const hasMapPreview = vectorMode
        ? vectorPageCount > 0
        : images.length > 0;

    const galleryProps = {
        images,
        isLoading,
        setOpenModalImageGallery,
        currentPage,
        setCurrentPage,
        vectorMode,
        vectorCuttingMap,
        vectorPageCount,
        drawEdge,
        onDetailHover: setActiveDetail,
        showRuler,
        rulerCoords,
        onRulerCoordsChange,
        rulerLocked: rulerEditing || rulerPinned,
        onRulerPinToggle,
    };

    return (

        <Box sx={{
            display: 'flex',
            alignItems: 'flex-start',
            marginTop: '10px'
        }}>
            <Box sx={{
                position: 'relative',
                display: 'flex',
                border: vectorMode
                    ? '2px solid #3B8BEC'
                    : hasMapPreview ? "none" : '2px solid #3B8BEC',
                boxSizing: 'border-box',
                height: vectorMode ? '740px' : '682px',
                width: vectorMode ? '930px' : '900px',
                marginLeft: vectorMode ? '-10px' : 0,
                marginBottom: vectorMode ? '40px' : 0,
            }}>
                <CuttingImageGallery
                    {...galleryProps}
                    weight='97%'
                    height='95%'
                    buttonClose={false}
                />
                <ModalImageGallery
                    {...galleryProps}
                    weight='64%'
                    height='91%'
                    open={openModalImageGallery}
                    onClose={() => setOpenModalImageGallery(false)}
                />
            </Box>
            <Box sx={{
                marginLeft: '20px',
                paddingRight: '3px',
                width: vectorMode ? '820px' : undefined,
            }}>
                {vectorMode && <ActiveDetailPreview detail={activeDetail}/>}
                {vectorMode ? (
                    <Box sx={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(2, minmax(0, 1fr))',
                        columnGap: 2,
                        alignItems: 'flex-start',
                    }}>
                        <Stack direction="column" spacing={0.1}>
                            <Box sx={{pl: 1.5}}>
                                <Tooltip title="Крест на карте: наведение курсора или ручной ввод X/Y в мм от внутреннего угла листа (Y вниз)">
                                    <FormControlLabel
                                        control={
                                            <Checkbox
                                                checked={showRuler}
                                                onChange={(event) => setShowRuler(event.target.checked)}
                                            />
                                        }
                                        label="Линейка / координаты"
                                    />
                                </Tooltip>
                                <Box sx={{display: 'flex', gap: 1, mt: 0.5, alignItems: 'center', pl: '42px'}}>
                                    <TextField
                                        label="X, мм"
                                        type="number"
                                        size="small"
                                        value={rulerInputX}
                                        inputProps={{
                                            min: 0,
                                            max: rulerMaxX || undefined,
                                            step: 1,
                                        }}
                                        onFocus={() => setRulerEditing(true)}
                                        onChange={(event) => handleRulerInputChange('x', event.target.value)}
                                        onBlur={() => applyRulerInputs()}
                                        onKeyDown={(event) => {
                                            if (event.key === 'Enter') {
                                                applyRulerInputs();
                                                event.target.blur();
                                            }
                                        }}
                                        sx={{width: 120}}
                                    />
                                    <TextField
                                        label="Y, мм"
                                        type="number"
                                        size="small"
                                        value={rulerInputY}
                                        inputProps={{
                                            min: 0,
                                            max: rulerMaxY || undefined,
                                            step: 1,
                                        }}
                                        onFocus={() => setRulerEditing(true)}
                                        onChange={(event) => handleRulerInputChange('y', event.target.value)}
                                        onBlur={() => applyRulerInputs()}
                                        onKeyDown={(event) => {
                                            if (event.key === 'Enter') {
                                                applyRulerInputs();
                                                event.target.blur();
                                            }
                                        }}
                                        sx={{width: 120}}
                                    />
                                </Box>
                            </Box>
                            <Tooltip
                                title={'Включает опцию при которой не объединяются обрезки и отходы меньше минимального обрезка, а также обрезки и отходы менее заданного размера не участвуют в раскрое обрезков и отходов с резерва'}>
                                <FormControlLabel
                                    control={<Checkbox
                                        disabled={!combiningDrops}
                                        onChange={() => handleCheckbox('takeAccountDimensions')}
                                        checked={takeAccountDimensions}
                                    />}
                                    label="Учет минимального обрезка"
                                />
                            </Tooltip>
                            <Tooltip title={'Разрешает кроить отходы на карте со склада резервов'}>
                                <FormControlLabel
                                    control={<Checkbox
                                        onChange={() => handleCheckbox('cutWastes')}
                                        checked={cutWastes}
                                    />}
                                    label="Кроить отходы на карте"
                                />
                            </Tooltip>
                            <Tooltip title={'Подгоняет под полосу на всех листах начиная со 2-го'}>
                                <FormControlLabel
                                    control={<Checkbox
                                        disabled={!fitToStripFirst}
                                        onChange={() => handleCheckbox('fitToStripSecond')}
                                        checked={fitToStripSecond}
                                    />}
                                    label="Подгонка полосы со 2 листа"
                                />
                            </Tooltip>
                            <Tooltip title={'Включает опцию при которой объединяются обрезки и отходы'}>
                                <FormControlLabel
                                    control={<Checkbox
                                        onChange={() => handleCheckbox('combiningDrops')}
                                        checked={combiningDrops}
                                    />}
                                    label="Объединение обрезков и отходов"
                                />
                            </Tooltip>
                            <Tooltip title={'Разрешает кроить вторичный обрезок на 1 листе со склада резервов'}>
                                <FormControlLabel
                                    control={<Checkbox
                                        disabled={!cutDropDetail ? true : !fitToStripFirst}
                                        onChange={() => handleCheckbox('cutFitToStripFirstDropDetail')}
                                        checked={cutFitToStripFirstDropDetail}
                                    />}
                                    label="Кроить вторичный обрезок на 1 листе"
                                />
                            </Tooltip>
                            <Tooltip
                                title={'Разрешает кроить вторичные обрезки комбинированных обрезков со склада резервов'}>
                                <FormControlLabel
                                    control={<Checkbox
                                        disabled={!cutDropDetail ? true : !combiningDrops}
                                        onChange={() => handleCheckbox('cutCombiningDrops')}
                                        checked={cutCombiningDrops}
                                    />}
                                    label="Кроить вторичные обрезки комбинированных обрезков"
                                />
                            </Tooltip>
                        </Stack>
                        <Stack direction="column" spacing={0.1}>
                            <Tooltip title={'Разрешает кроить первичные обрезки на карте со склада резервов'}>
                                <FormControlLabel
                                    control={<Checkbox
                                        onChange={() => handleCheckbox('cutCutOffs')}
                                        checked={cutCutOffs}
                                    />}
                                    label="Кроить первичные обрезки"
                                />
                            </Tooltip>
                            <Tooltip title={'Подгоняет под полосу на 1 листе'}>
                                <FormControlLabel
                                    control={<Checkbox
                                        onChange={() => handleCheckbox('fitToStripFirst')}
                                        checked={fitToStripFirst}
                                    />}
                                    label="Подгонка полосы на 1 листе"
                                />
                            </Tooltip>
                            <Box sx={{pl: '42px'}}>
                                <CuttingTextField
                                    label="Направление"
                                    value={dimensions}
                                    setValue={setDimensions}
                                    field={'dimension'}
                                    disabled={!fitToStripFirst}
                                />
                            </Box>
                            <Tooltip title={'Разрешить кроить вторичные обрезки деталями со склада резервов'}>
                                <FormControlLabel
                                    control={<Checkbox
                                        onChange={() => handleCheckbox('cutDropDetail')}
                                        checked={cutDropDetail}
                                    />}
                                    label="Кроить вторичные обрезки"
                                />
                            </Tooltip>
                            <Tooltip title={'Разрешает кроить вторичные обрезоки на со 2-го листа со склада резервов'}>
                                <FormControlLabel
                                    control={<Checkbox
                                        disabled={!cutDropDetail ? true : !fitToStripFirst ? true : !fitToStripSecond}
                                        onChange={() => handleCheckbox('cutFitToStripSecondDropDetail')}
                                        checked={cutFitToStripSecondDropDetail}
                                    />}
                                    label="Кроить вторичные обрезоки со 2-го листа"
                                />
                            </Tooltip>
                            <Tooltip title={'Кроить только склад'}>
                                <FormControlLabel
                                    control={<Checkbox
                                        onChange={() => handleCheckbox('cutOnlyWarehouse')}
                                        checked={cutOnlyWarehouse}
                                    />}
                                    label="Кроить только склад"
                                />
                            </Tooltip>
                            <Tooltip title={'Кроить только обрезки'}>
                                <FormControlLabel
                                    control={<Checkbox
                                        onChange={() => handleCheckbox('сutOnlyCutOffs')}
                                        checked={cutOnlyCutOffs}
                                    />}
                                    label="Кроить только обрезки"
                                />
                            </Tooltip>
                        </Stack>
                    </Box>
                ) : (
                <Stack
                    direction="column"
                    spacing={0.1}
                >
                    <Box sx={{width: '400px', marginRight: '20px'}}>
                        <Box sx={{width: '400px', marginRight: '20px'}}>
                            <Tooltip
                                title={'Включает опцию при которой не объединяются обрезки и отходы меньше минимального обрезка, а также обрезки и отходы менее заданного размера не участвуют в раскрое обрезков и отходов с резерва'}>
                                <FormControlLabel
                                    control={<Checkbox
                                        disabled={!combiningDrops}
                                        onChange={() => handleCheckbox('takeAccountDimensions')}
                                        checked={takeAccountDimensions}
                                    />}
                                    label="Учет минимального обрезка"
                                />
                            </Tooltip>
                        </Box>
                        <Box sx={{width: '400px', marginRight: '20px'}}>
                            <Tooltip title={'Разрешает кроить первичные обрезки на карте со склада резервов'}>
                                <FormControlLabel
                                    control={<Checkbox
                                        onChange={() => handleCheckbox('cutCutOffs')}
                                        checked={cutCutOffs}
                                    />}
                                    label="Кроить первичные обрезки"
                                />
                            </Tooltip>
                        </Box>
                        <Box sx={{width: '400px', marginRight: '20px'}}>
                            <Tooltip title={'Разрешает кроить отходы на карте со склада резервов'}>
                                <FormControlLabel
                                    control={<Checkbox
                                        onChange={() => handleCheckbox('cutWastes')}
                                        checked={cutWastes}
                                    />}
                                    label="Кроить отходы на карте"
                                />
                            </Tooltip>
                        </Box>
                        <Tooltip title={'Подгоняет под полосу на 1 листе'}>
                            <FormControlLabel
                                control={<Checkbox
                                    onChange={() => handleCheckbox('fitToStripFirst')}
                                    checked={fitToStripFirst}
                                />}
                                label="Подгонка полосы на 1 листе"
                            />
                        </Tooltip>
                    </Box>
                    <Box sx={{width: '400px', marginRight: '20px'}}>
                        <Tooltip title={'Подгоняет под полосу на всех листах начиная со 2-го'}>
                            <FormControlLabel
                                control={<Checkbox
                                    disabled={!fitToStripFirst}
                                    onChange={() => handleCheckbox('fitToStripSecond')}
                                    checked={fitToStripSecond}
                                />}
                                label="Подгонка полосы со 2 листа"
                            />
                        </Tooltip>
                    </Box>
                    <CuttingTextField
                        label="Направление"
                        value={dimensions}
                        setValue={setDimensions}
                        field={'dimension'}
                        disabled={!fitToStripFirst}
                    />
                    <Box sx={{width: '400px', marginRight: '20px'}}>
                        <Tooltip title={'Включает опцию при которой объединяются обрезки и отходы'}>
                            <FormControlLabel
                                control={<Checkbox
                                    onChange={() => handleCheckbox('combiningDrops')}
                                    checked={combiningDrops}
                                />}
                                label="Объединение обрезков и отходов"
                            />
                        </Tooltip>
                    </Box>


                    <Box sx={{width: '400px', marginRight: '20px'}}>
                        <Tooltip title={'Разрешить кроить вторичные обрезки деталями со склада резервов'}>
                            <FormControlLabel
                                control={<Checkbox
                                    onChange={() => handleCheckbox('cutDropDetail')}
                                    checked={cutDropDetail}
                                />}
                                label="Кроить вторичные обрезки"
                            />
                        </Tooltip>
                    </Box>
                    <Box sx={{width: '400px', marginRight: '20px'}}>
                        <Tooltip title={'Разрешает кроить вторичный обрезок на 1 листе со склада резервов'}>
                            <FormControlLabel
                                control={<Checkbox
                                    disabled={!cutDropDetail ? true : !fitToStripFirst}
                                    onChange={() => handleCheckbox('cutFitToStripFirstDropDetail')}
                                    checked={cutFitToStripFirstDropDetail}
                                />}
                                label="Кроить вторичный обрезок на 1 листе"
                            />
                        </Tooltip>
                    </Box>
                    <Box sx={{width: '400px', marginRight: '20px'}}>
                        <Tooltip title={'Разрешает кроить вторичные обрезоки на со 2-го листа со склада резервов'}>
                            <FormControlLabel
                                control={<Checkbox
                                    disabled={!cutDropDetail ? true : !fitToStripFirst ? true : !fitToStripSecond}
                                    onChange={() => handleCheckbox('cutFitToStripSecondDropDetail')}
                                    checked={cutFitToStripSecondDropDetail}
                                />}
                                label="Кроить вторичные обрезоки со 2-го листа"
                            />
                        </Tooltip>
                    </Box>
                    <Box sx={{width: '400px', marginRight: '20px'}}>
                        <Tooltip
                            title={'Разрешает кроить вторичные обрезки комбинированных обрезков со склада резервов'}>
                            <FormControlLabel
                                control={<Checkbox
                                    disabled={!cutDropDetail ? true : !combiningDrops}
                                    onChange={() => handleCheckbox('cutCombiningDrops')}
                                    checked={cutCombiningDrops}
                                />}
                                label="Кроить вторичные обрезки комбинированных обрезков"
                            />
                        </Tooltip>
                    </Box>

                    <Box sx={{
                        display: 'flex',
                        flexDirection: 'row',
                        alignItems: 'flex-start',
                        '& > *': {
                            marginRight: '10px',
                        }
                    }}>
                        <Tooltip title={'Кроить только склад'}>
                            <FormControlLabel
                                control={<Checkbox
                                    onChange={() => handleCheckbox('cutOnlyWarehouse')}
                                    checked={cutOnlyWarehouse}
                                />}
                                label="Кроить только склад"
                            />
                        </Tooltip>
                        <Tooltip title={'Кроить только обрезки'}>
                            <FormControlLabel
                                control={<Checkbox
                                    onChange={() => handleCheckbox('сutOnlyCutOffs')}
                                    checked={cutOnlyCutOffs}
                                />}
                                label="Кроить только обрезки"
                            />
                        </Tooltip>
                    </Box>
                </Stack>
                )}
            </Box>
        </Box>
    );
};

export default CuttingModule;
